import React, { useCallback, useRef, useState } from 'react'
import cx from 'classnames'
import { useAppDispatch, useAppSelector } from 'reduxTyped'
import { Checkbox, l, Toggle } from '@manychat/manyui'

import { WA_LIST_MESSAGE_MAX_DESCRIPTION_LENGTH } from 'apps/whatsApp/constants/listMessageConstants'
import { getUpdatedListMessageSections } from 'apps/whatsApp/helpers/getUpdatedListMessageSections'
import { isListMessageBlock } from 'apps/whatsApp/ListMessages/helpers/isListMessageBlock'
import { isListMessageButton } from 'apps/whatsApp/ListMessages/helpers/isListMessageButton'
import { NodeType } from 'common/builder'
import * as buttonActions from 'common/builder/actions/buttonActions'
import { updateBlock } from 'common/builder/actions/updateBlock'
import { IButton, IURLButton } from 'common/builder/buttons/buttonInterfaces'
import { isSmartLink, isUrlButton } from 'common/builder/buttons/buttonTypeHelpers'
import ButtonTitleInput from 'common/builder/components/builder/ButtonForm/ButtonTitleInput'
import BuyButtonForm from 'common/builder/components/builder/ButtonForm/BuyButtonForm'
import FlowTextEditor from 'common/builder/components/misc/FlowTextEditor'
import { BlockType } from 'common/builder/constants/BlockType'
import { ButtonType } from 'common/builder/constants/ButtonType'
import {
  getIsFormula,
  getPhoneInputType,
  PhoneInputType,
  PhoneInputTypeOptions,
} from 'common/builder/constants/PhoneInputType'
import ButtonNextStepPane from 'common/builder/controllers/builder/ButtonNextStepPane'
import ButtonNextStepSelect from 'common/builder/controllers/builder/ButtonNextStepSelect'
import ButtonModel from 'common/builder/models/Button'
import { NextStepType } from 'common/builder/models/Source/constants'
import { INode } from 'common/builder/nodes/nodeInterfaces'
import builderSelectors from 'common/builder/selectors/builder'
import * as builderStateSelectors from 'common/builder/selectors/builder/builderStateSelectors'
import { getOTNButtonCaption } from 'common/builder/selectors/builder/buttonSelectors'
import * as entitySelectors from 'common/builder/selectors/builder/entitySelectors'
import * as nextStepSelectors from 'common/builder/selectors/builder/nextStepSelectors'
import * as sourceSelectors from 'common/builder/selectors/builder/sourceSelectors'
import { isNextStepTypeUrl } from 'common/builder/utils/isNextStepTypeUrl'
import { WebViewOptions, WebViewSize, WebViewSizeTooltip } from 'common/core/constants/WebViewSize'
import { getCurrentAccount } from 'common/core/selectors/appSelectors'
import { FieldGroupId, FieldType } from 'common/fields/entities/enums'
import { getSetPhoneFields } from 'common/fields/linking/accountAvailableFields'
import FieldPicker from 'common/fields/linking/components/FieldPicker'
import { Select } from 'components/forms/Select/Select'
import If from 'components/If/If'
import LabelTooltip from 'components/LabelTooltip'
import { IMCTextEditorRef } from 'components/MCTextEditor/MCTextEditor'
import SmartLinkPane from 'components/MCTextEditor/smartLinks/components/SmartLinksPane/SmartLinksPane'
import PhoneInput from 'components/PhoneInput'
import { IMinimalChangeEvent } from 'constants/constants'
import { AccountFlag } from 'utils/services/featureFlagsService/constants'
import { useAccountReleaseToggle } from 'utils/services/featureFlagsService/releaseTogglesHooks'

import { ButtonFormPoweredByMcBlock } from './ButtonFormPoweredByMcBlock'

interface ButtonFormProps {
  builderId: string
  buttonId: string
  className?: string
  errors: Array<{ prop: string }>
  showErrors: boolean
  dirty?: boolean
}

const ButtonForm = ({
  builderId,
  buttonId,
  className,
  errors,
  showErrors,
  dirty = false,
}: ButtonFormProps) => {
  const { button, parentNode } = useAppSelector((state) =>
    builderSelectors.button.getButtonInfo(state, builderId, buttonId),
  ) as { button: IButton; parentNode: INode }

  const config = useAppSelector((state) => builderStateSelectors.getById(state, builderId).config)

  let nextStepOptions = useAppSelector((state) =>
    nextStepSelectors.getNextStepOptions(state, builderId, buttonId),
  )

  if (config.allowedButtons) {
    nextStepOptions = nextStepOptions.filter((button) =>
      config.allowedButtons.includes(button.type),
    )
  }

  const isSimplifiedBroadcastPage =
    config.allowedButtons &&
    config.allowedButtons.length === 1 &&
    config.allowedButtons[0] === ButtonType.URL

  const isSmartLinkButton = isSmartLink(button)

  const target = useAppSelector((state) => sourceSelectors.getTarget(state, builderId, buttonId))
  const nextStepType = useAppSelector((state) =>
    sourceSelectors.getNextStepType(state, builderId, buttonId),
  )

  const { is_buy_button_enabled, payments_settings_enabled, payments_enabled } =
    useAppSelector(getCurrentAccount)

  // disableBuyButton - it is impossible to add a Buy button because the user is not an admin, and the Stripe account is not connected
  const disableBuyButton = payments_enabled && !payments_settings_enabled && !is_buy_button_enabled

  // disallowBuyButton - it is impossible to add a Buy button because it is not the appropriate block type
  const buttonBlock = useAppSelector((state) =>
    entitySelectors.getParentBlock(state, builderId, buttonId),
  )
  const disallowBuyButton =
    buttonBlock && [BlockType.TEXT, BlockType.LIST].includes(buttonBlock.type)

  const otnButtonCaption = useAppSelector((state) =>
    getOTNButtonCaption(state, builderId, buttonId),
  )

  const {
    config: { isSimple, disableContentPersonalization },
  } = useAppSelector((state) => builderSelectors.builderState.getById(state, builderId))

  const fields = useAppSelector(getSetPhoneFields)

  const dispatch = useAppDispatch()

  const onChange = useCallback(
    (changes: UnsafeAnyObject) => {
      dispatch(buttonActions.updateButton(builderId, buttonId, changes))
    },
    [builderId, buttonId, dispatch],
  )

  const [urlDirty, setUrlDirty] = useState(false)
  const [phoneDirty, setPhoneDirty] = useState(false)

  const handleUrlBlur = () => {
    onChange({ url: (button as IURLButton).url.trim() })
    setUrlDirty(true)
  }
  const handlePhoneBlur = () => setPhoneDirty(true)

  const handleBlur = () => {
    if (isNextStepTypeUrl(nextStepType)) {
      handleUrlBlur()
    }
    if (nextStepType === NextStepType.CALL) {
      handlePhoneBlur()
    }
  }

  const urlInput = useRef<IMCTextEditorRef>(null)
  const phoneInput = useRef<PhoneInput>(null)

  const setAttr = (event: IMinimalChangeEvent) => {
    const { value, name } = event.target
    const isString = typeof value === 'string'
    if (name) {
      onChange({ [name]: isString ? value.replace(/(\n|\r|×)/gm, '') : value })

      if (
        isListMessageButton(button) &&
        buttonBlock !== undefined &&
        isListMessageBlock(buttonBlock)
      ) {
        const updatedSections = getUpdatedListMessageSections(buttonBlock.sections, button.id, {
          [name]: value,
        })

        if (updatedSections !== null) {
          dispatch(
            updateBlock(builderId, buttonBlock.id, {
              sections: updatedSections,
            }),
          )
        }
      }
    }
  }

  const handleCaptionClick = () => {
    const input = urlInput || phoneInput
    input?.current?.focus()
  }

  const handleEnableWebviewChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target

    const changes: { enableWebview: boolean; webviewSize?: WebViewSize } = {
      enableWebview: checked,
    }

    if (checked && (button as IURLButton).webviewSize === WebViewSize.DEFAULT) {
      changes.webviewSize = WebViewSize.FULL
    }

    onChange(changes)
  }

  const getPhoneUrlError = () => {
    if (isNextStepTypeUrl(nextStepType)) {
      return errors.find((err) => err.prop === 'url') && (showErrors || dirty || urlDirty)
    }
    if (nextStepType === NextStepType.CALL) {
      return errors.find((err) => err.prop === 'phone') && (showErrors || dirty || phoneDirty)
    }

    return false
  }

  const setFormula = (event: React.ChangeEvent<HTMLSelectElement>) => {
    // clear phone input value on type change
    onChange({ is_formula: getIsFormula(event.target.value as PhoneInputType), phone: '' })
  }

  const isWebViewEnabled = useAccountReleaseToggle(AccountFlag.ENABLE_WEBVIEW)

  const isInOTNRequestBlock = buttonBlock?.type === BlockType.ONE_TIME_NOTIFY_REQUEST
  const isInDynamicBlock = buttonBlock?.type === BlockType.DYNAMIC

  let nextStepLabel = l.translate('When this button is pressed')
  if (isInDynamicBlock) {
    nextStepLabel = l.translate('Select a Step for a request error case')
  } else if (buttonBlock?.type === BlockType.EMAIL_IMAGE) {
    nextStepLabel = l.translate('When This Image is Clicked')
  } else if (buttonBlock?.type === BlockType.EMAIL_BUTTON) {
    nextStepLabel = l.translate('When This Button is Clicked')
  } else if (isSmartLinkButton) {
    nextStepLabel = l.translate('When This Link is Clicked')
  }

  const isMessageTemplateButtonBlock = buttonBlock?.type === BlockType.MESSAGE_TEMPLATE
  const disableWebviewSize =
    parentNode?.nodeType !== NodeType.CONTENT || isMessageTemplateButtonBlock || isSmartLinkButton

  const hasTarget = Boolean(target)

  const handleResetButtonNextStep = useCallback(() => {
    dispatch(
      buttonActions.resetButtonNextStep(builderId, buttonId, { keepType: true, keepTarget: null }),
    )
  }, [builderId, buttonId, dispatch])

  const handleSelectButtonNextStep = useCallback(
    (nextStepType: NextStepType) => {
      dispatch(
        buttonActions.selectButtonNextStep(builderId, buttonId, {
          nextStepType,
          keepType: true,
          nodeId: null,
        }),
      )
    },
    [builderId, buttonId, dispatch],
  )

  const handleToggleAdditionalAction = useCallback(() => {
    if (hasTarget) {
      handleResetButtonNextStep()
      return
    }

    handleSelectButtonNextStep(NextStepType.ACTION_GROUP)
  }, [hasTarget, handleResetButtonNextStep, handleSelectButtonNextStep])

  if (button.type === ButtonType.BUY) {
    return (
      <BuyButtonForm
        builderId={builderId}
        button={button}
        errors={errors}
        showErrors={showErrors}
        onChange={onChange}
      />
    )
  }

  return (
    <div className={className}>
      <If condition={!isInDynamicBlock && !isSmartLinkButton}>
        <ButtonTitleInput
          builderId={builderId}
          value={otnButtonCaption || ButtonModel.getCaption(button)}
          onChange={setAttr}
          disabled={
            button.type === ButtonType.SHARE || isMessageTemplateButtonBlock || isInOTNRequestBlock
          }
          disabledTooltip={
            isInOTNRequestBlock
              ? l.translate(
                  'This text cannot be changed due to Meta policies. Also, contacts will see it in their language.',
                )
              : undefined
          }
          errors={errors}
          showErrors={showErrors}
          withVariables={!isListMessageButton(button)}
        />
      </If>

      {isListMessageButton(button) && (
        <div className="m-t-xs">
          <label className="form-label">{l.translate('Description')}</label>
          <FlowTextEditor
            multiline={false}
            limit={WA_LIST_MESSAGE_MAX_DESCRIPTION_LENGTH}
            name="description"
            onChange={setAttr}
            value={button.description || ''}
            placeholder={l.translate('Enter a description...')}
            withEmoji={false}
            withVariables={false}
            withDefaultStyles
          />
        </div>
      )}

      <label className="form-label m-b-xs" htmlFor="button-type">
        {nextStepLabel}
      </label>

      {isSmartLinkButton &&
        [ButtonType.CONTENT, ButtonType.CROSS_CHANNEL_CONTENT].includes(button.type) && (
          <>
            <SmartLinkPane
              builderId={builderId}
              buttonId={button.id}
              type={button.type}
              channel={'channel' in button ? button.channel : null}
              className="m-t"
            />
            <div className="m-t-xs m-b text-secondary">
              {l.translate(
                'Contacts will be automatically redirected to messenger and opt-ined. You will be able to interact with them for free during 24-hours',
              )}
            </div>
          </>
        )}

      {nextStepType ? (
        <>
          <ButtonNextStepPane
            type={nextStepType}
            builderId={builderId}
            buttonId={button.id}
            button={button}
            target={target}
            keepTarget={button.type === ButtonType.URL}
            onCaptionClick={handleCaptionClick}
            keepType={isSmartLinkButton}
          />

          {isUrlButton(button) && (
            <>
              <label className="form-label m-b-xs">{l.translate('Website URL')}</label>
              <FlowTextEditor
                maxRows={6}
                minRows={1}
                ref={urlInput}
                name="url"
                noLineBreaks
                onChange={setAttr}
                onBlur={handleBlur}
                value={button.url}
                placeholder="https://yourwebsite.com"
                isValid={!getPhoneUrlError()}
                withVariables={!disableContentPersonalization}
                disabled={isMessageTemplateButtonBlock}
                withDefaultStyles
                data-onboarding-id="button-form-website-url-input"
              />

              <If condition={isWebViewEnabled}>
                <div>
                  <Checkbox
                    className="m-t"
                    name="enableWebview"
                    onChange={handleEnableWebviewChange}
                    label={l.translate('Enable webview SDK')}
                    checked={button.enableWebview}
                  />
                </div>
              </If>

              <If condition={!disableWebviewSize}>
                <label className="form-label m-b-xs d-flex align-center">
                  {l.translate('Webview size')}
                  <LabelTooltip>{WebViewSizeTooltip}</LabelTooltip>
                </label>
                <Select
                  name="webviewSize"
                  value={button.webviewSize}
                  onChange={setAttr}
                  options={
                    button.enableWebview
                      ? WebViewOptions.filter((option) => option.value !== WebViewSize.DEFAULT)
                      : WebViewOptions
                  }
                />
              </If>
            </>
          )}

          {button.type === ButtonType.CALL && (
            <div>
              <label className="form-label m-b-xs">{l.translate('Call number')}</label>
              <label className="text-secondary">{l.translate('Type')}</label>
              <Select
                key="phone-type-select"
                className="m-t-xxs m-b-md"
                id="select-phone-input-type"
                name="is_formula"
                options={PhoneInputTypeOptions}
                value={getPhoneInputType(Boolean(button.is_formula))}
                onChange={setFormula}
              />
              <label className="text-secondary">{l.translate('Phone')}</label>
              {button.is_formula ? (
                <FieldPicker
                  invalid={getPhoneUrlError()}
                  name="phone"
                  value={button.phone}
                  onSelect={setAttr}
                  fields={fields}
                  allowedTypes={[FieldType.NUMBER, FieldType.TEXT]}
                  allowNew={FieldGroupId.USER}
                  ariaLabel={l.translate('Phone')}
                />
              ) : (
                <PhoneInput
                  ref={phoneInput}
                  defaultCountry="us"
                  placeholder={l.translate('Enter phone number here...')}
                  className="form-control"
                  value={button.phone}
                  name="phone"
                  onBlur={handleBlur}
                  onChange={setAttr}
                  invalid={getPhoneUrlError()}
                />
              )}
            </div>
          )}
        </>
      ) : (
        <ButtonNextStepSelect
          options={nextStepOptions}
          builderId={builderId}
          // @ts-expect-error react-redux connect don't adds controller propTypes
          buttonId={button.id}
          disableBuyButton={disableBuyButton}
          disallowBuyButton={disallowBuyButton}
          keepType={button.type === ButtonType.WA_LIST_MESSAGE_BUTTON}
        />
      )}

      <If
        condition={
          buttonBlock?.type === BlockType.EMAIL_TEXT ||
          (parentNode?.nodeType === NodeType.TELEGRAM &&
            buttonBlock?.type === BlockType.TEXT &&
            isSmartLinkButton)
        }
      >
        <label className="form-label m-b-xs">{l.translate('Link text')}</label>
        <FlowTextEditor
          name="caption"
          multiline={false}
          onChange={setAttr}
          value={button.caption}
          placeholder={l.translate('Enter link text')}
          isValid
          withDefaultStyles
          withVariables
        />
      </If>

      <If
        condition={
          [ButtonType.URL, ButtonType.CTA_URL].includes(button.type) &&
          !isSimple &&
          !isMessageTemplateButtonBlock &&
          !isSimplifiedBroadcastPage
        }
      >
        <div className="m-t-lg">
          <Toggle
            onChange={handleToggleAdditionalAction}
            value={hasTarget}
            checked={hasTarget}
            className="d-flex m-t-0 m-b-xxs"
            orientation="left"
            fullWidth
            label={l.translate('Additional actions')}
          />
          <div
            className={cx('text-secondary text-sm', {
              'm-b': hasTarget,
            })}
          >
            {l.translate('E.g. you can add a tag on a button click.')}
          </div>
          {hasTarget && (
            <ButtonNextStepPane
              type={NextStepType.ACTION_GROUP}
              builderId={builderId}
              buttonId={button.id}
              button={button}
              target={target}
              keepType
              disableReset
            />
          )}
        </div>
      </If>
      <ButtonFormPoweredByMcBlock button={button} parentNodeType={parentNode?.nodeType} />
    </div>
  )
}

export default ButtonForm
